import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Key, Copy, Trash2, AlertTriangle } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatDate, formatDateTime } from '../../utils/datetime';

/**
 * Voice agent (AIVA) API key card. The mySMS360 backend issues a
 * facility-scoped key the operator pastes into Vapi's get_availability
 * + voice_forward_number tools as the X-API-Key header. Key is shown
 * ONCE at mint time -- only fingerprints are retrievable later.
 *
 * Previously lived inline in CommunicationSection.js under the comms
 * Settings sub-tab. Moved to the AIVA page (/intelligence Settings tab)
 * on 2026-06-01 because it's AIVA-specific config, not comms-channel
 * config. Backend endpoints unchanged:
 *   GET    /api/facilities/{fid}/voice/api-keys
 *   POST   /api/facilities/{fid}/voice/api-keys/mint
 *   POST   /api/facilities/{fid}/voice/api-keys/{fingerprint}/revoke
 */
const VoiceAgentKeyCard = ({ facility }) => {
  const facilityId = facility?._id || facility?.id;
  const timeZone = facility?.timezone || undefined;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);
  const [mintedKey, setMintedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true); setError(null);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/voice/api-keys`);
      setKeys(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const onMint = async () => {
    setMinting(true); setError(null);
    try {
      const r = await apiClient.post(`/api/facilities/${facilityId}/voice/api-keys/mint`);
      setMintedKey(r.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setMinting(false); }
  };

  const onRevoke = async (fingerprint) => {
    if (!window.confirm(`Revoke voice key ${fingerprint}? Any Vapi tool using this key will start failing immediately.`)) return;
    setError(null);
    try {
      await apiClient.post(`/api/facilities/${facilityId}/voice/api-keys/${fingerprint}/revoke`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(mintedKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const active = keys.filter((k) => k.is_active);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-2xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
            <Bot className="h-4 w-4 text-slate-400" />
            Voice agent (AIVA) API key
          </h2>
          <p className="text-[12px] text-slate-500 mt-1">
            Paste this into your Vapi assistant&rsquo;s <code>X-API-Key</code> header for the <code>get_availability</code> and <code>voice_forward_number</code> tools. Scoped to this facility only.
          </p>
        </div>
        <button onClick={onMint} disabled={minting || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[12px] font-medium flex-shrink-0">
          <Key className="h-3.5 w-3.5" /> {minting ? 'Minting...' : 'Mint new key'}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {mintedKey && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-[12px] font-semibold text-amber-900 mb-1.5">
            Copy this key now &mdash; it will not be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all text-[12px] bg-white border border-amber-200 rounded px-2 py-1.5 font-mono">
              {mintedKey.key}
            </code>
            <button onClick={onCopy}
                    className="inline-flex items-center gap-1 px-2 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-[11px] font-medium">
              <Copy className="h-3 w-3" /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-[11px] text-amber-800 mt-1.5">
            Fingerprint <code>{mintedKey.fingerprint}</code> &middot; minted {formatDateTime(mintedKey.created_at, timeZone)}
          </p>
        </div>
      )}

      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Active keys</p>
        {loading && <p className="text-[12px] text-slate-400">Loading...</p>}
        {!loading && active.length === 0 && (
          <p className="text-[12px] text-slate-500">No active keys. Click &ldquo;Mint new key&rdquo; to create one.</p>
        )}
        {!loading && active.length > 0 && (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
            {active.map((k) => (
              <li key={k.fingerprint} className="flex items-center justify-between px-3 py-2 text-[12px]">
                <div>
                  <code className="font-mono text-slate-700">{k.fingerprint}</code>
                  <span className="text-slate-400 ml-2">minted {formatDate(k.created_at, timeZone)}</span>
                </div>
                <button onClick={() => onRevoke(k.fingerprint)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-rose-600 hover:bg-rose-50 rounded text-[11px]">
                  <Trash2 className="h-3 w-3" /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default VoiceAgentKeyCard;
